import type { ReactNode } from 'react';
import { Typography } from 'antd';
import {
  CheckSquareOutlined,
  EditOutlined,
  FileTextOutlined,
  MailOutlined,
  MergeCellsOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  TagOutlined,
  TeamOutlined,
  FunnelPlotOutlined,
  SafetyCertificateOutlined,
  FormOutlined,
  ImportOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import type { ActivityItem } from '@crm/shared';
import { EntityTag } from './EntityTag';
import { fileUrl } from '../api/client';

/** Web addresses in a note open in a new tab; the app's own file links (a scanned card's photo) read "photo". */
const LINK = /(https?:\/\/[^\s)]+|\/api\/files\/[0-9a-f-]{36})/g;
function linkify(text: string): ReactNode[] {
  return text.split(LINK).map((part, i) =>
    i % 2 === 0 ? (
      part
    ) : (
      <a key={i} href={part.startsWith('/api/files/') ? fileUrl(part.slice('/api/files/'.length)) : part} target="_blank" rel="noreferrer">
        {part.startsWith('/api/files/') ? 'photo' : part}
      </a>
    ),
  );
}

export function activityIcon(type: ActivityItem['type']): ReactNode {
  if (type === 'NOTE') return <FileTextOutlined />;
  if (type === 'CALL') return <PhoneOutlined />;
  if (type === 'MEETING') return <TeamOutlined />;
  if (type.startsWith('EMAIL_') || type === 'UNSUBSCRIBED') return <MailOutlined />;
  if (type.startsWith('TASK_')) return <CheckSquareOutlined />;
  if (type.startsWith('TAG_') || type.startsWith('LIST_') || type.startsWith('SEGMENT_')) return <TagOutlined />;
  if (type.startsWith('DEAL_') || type === 'STAGE_CHANGED') return <FunnelPlotOutlined />;
  if (type === 'FIELD_CHANGED') return <EditOutlined />;
  if (type === 'CONTACT_CREATED') return <PlusCircleOutlined />;
  if (type === 'ORGANIZATION_CHANGED') return <ApartmentOutlined />;
  if (type === 'MERGED' || type === 'UNMERGED') return <MergeCellsOutlined />;
  if (type === 'VERIFIED') return <SafetyCertificateOutlined />;
  if (type === 'FORM_SUBMITTED') return <FormOutlined />;
  if (type === 'IMPORTED') return <ImportOutlined />;
  return <EditOutlined />;
}

const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : Array.isArray(v) ? v.join(', ') : String(v));

/** Renders the human-readable body of an activity from its type and payload. */
export function ActivityText({ a, labels = {}, showContact, showDeal }: { a: ActivityItem; labels?: Record<string, string>; showContact?: boolean; showDeal?: boolean }) {
  const p = a.payload as Record<string, unknown>;
  const who = a.actorUser?.name ?? a.agent?.name ?? 'System';
  const contact =
    showContact && a.contact ? (
      <>
        {' '}
        · <EntityTag kind="contact" id={a.contact.id}>{[a.contact.firstName, a.contact.lastName].filter(Boolean).join(' ') || a.contact.email}</EntityTag>
      </>
    ) : null;

  switch (a.type) {
    case 'NOTE':
    case 'CALL':
    case 'MEETING':
      return (
        <div>
          <Typography.Text strong>{who}</Typography.Text> logged a {a.type.toLowerCase()}
          {contact}
          {showDeal && a.deal && (
            <>
              {' '}
              on <EntityTag kind="deal" id={a.deal.id}>{a.deal.title}</EntityTag>
              {a.deal.pipeline && <Typography.Text type="secondary"> ({a.deal.pipeline.name})</Typography.Text>}
            </>
          )}
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>{linkify(String(p.body ?? ''))}</Typography.Paragraph>
        </div>
      );
    case 'FIELD_CHANGED': {
      const changes = (p.changes as { field: string; from: unknown; to: unknown }[] | undefined) ?? [];
      return (
        <div>
          <Typography.Text strong>{who}</Typography.Text> updated {changes.length} field{changes.length === 1 ? '' : 's'}
          {contact}
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {changes.map((c) => (
              <li key={c.field}>
                <Typography.Text type="secondary">{labels[c.field] ?? c.field}:</Typography.Text> {fmt(c.from)} → {fmt(c.to)}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    case 'CONTACT_CREATED':
      return (
        <span>
          <Typography.Text strong>{who}</Typography.Text> created the contact{p.source && p.source !== 'manual' ? ` via ${String(p.source)}` : ''}
          {contact}
        </span>
      );
    case 'ORGANIZATION_CHANGED':
      if (p.created) return <span><Typography.Text strong>{who}</Typography.Text> created the organization</span>;
      if (p.changes) {
        const changes = p.changes as { field: string; from: unknown; to: unknown }[];
        return (
          <div>
            <Typography.Text strong>{who}</Typography.Text> updated the organization
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {changes.map((c) => (
                <li key={c.field}>
                  <Typography.Text type="secondary">{labels[c.field] ?? c.field}:</Typography.Text> {fmt(c.from)} → {fmt(c.to)}
                </li>
              ))}
            </ul>
          </div>
        );
      }
      if (p.added) return <span><Typography.Text strong>{who}</Typography.Text> added <b>{String(p.added)}</b> to the contact's organizations{p.primary === true ? ' as primary' : ''}{contact}</span>;
      if (p.removed) return <span><Typography.Text strong>{who}</Typography.Text> removed <b>{String(p.removed)}</b> from the contact's organizations{contact}</span>;
      if (typeof p.primary === 'string') return <span><Typography.Text strong>{who}</Typography.Text> made <b>{p.primary}</b> the contact's primary organization{contact}</span>;
      return <span><Typography.Text strong>{who}</Typography.Text> changed the contact's organization{contact}</span>;
    case 'TASK_CREATED':
      return <span><Typography.Text strong>{who}</Typography.Text> created task “{String(p.title ?? '')}”{contact}</span>;
    case 'TASK_DONE':
      return <span><Typography.Text strong>{who}</Typography.Text> completed task “{String(p.title ?? '')}”{contact}</span>;
    case 'TAG_ADDED':
      return <span>Tag <b>{String(p.tagName ?? '')}</b> added{contact}</span>;
    case 'TAG_REMOVED':
      return <span>Tag <b>{String(p.tagName ?? '')}</b> removed{contact}</span>;
    case 'LIST_ADDED':
      return <span>Added to list <b>{String(p.listName ?? '')}</b>{contact}</span>;
    case 'LIST_REMOVED':
      return <span>Removed from list <b>{String(p.listName ?? '')}</b>{contact}</span>;
    case 'SEGMENT_ENTERED':
      return <span>Entered segment <b>{String(p.segmentName ?? '')}</b>{contact}</span>;
    case 'SEGMENT_LEFT':
      return <span>Left segment <b>{String(p.segmentName ?? '')}</b>{contact}</span>;
    case 'EMAIL_SENT':
      return <span>{a.agent?.name ?? who} sent “{String(p.subject ?? '')}”{contact}</span>;
    case 'EMAIL_RECEIVED':
      return <span>Replied: “{String(p.subject ?? '')}”{contact}</span>;
    case 'EMAIL_OPENED':
      return <span>Opened “{String(p.subject ?? '')}”{contact}</span>;
    case 'EMAIL_CLICKED':
      return <span>Clicked a link in “{String(p.subject ?? '')}”{contact}</span>;
    case 'EMAIL_BOUNCED':
      return <span>Email bounced{p.subject ? `: “${String(p.subject)}”` : ''}{contact}</span>;
    case 'EMAIL_COMPLAINED':
      return <span>Marked an email as spam{contact}</span>;
    case 'UNSUBSCRIBED':
      return <span>Unsubscribed from emails{contact}</span>;
    case 'STAGE_CHANGED':
      return <span><Typography.Text strong>{who}</Typography.Text> moved deal “{String(p.title ?? '')}” from {fmt(p.from)} to {fmt(p.to)}{contact}</span>;
    case 'DEAL_CREATED':
      return <span><Typography.Text strong>{who}</Typography.Text> created deal “{String(p.title ?? '')}”{contact}</span>;
    case 'DEAL_WON':
      return <span>Deal “{String(p.title ?? '')}” won{contact}</span>;
    case 'DEAL_LOST':
      return <span>Deal “{String(p.title ?? '')}” lost{p.reason ? ` (${String(p.reason)})` : ''}{contact}</span>;
    case 'FORM_SUBMITTED':
      return <span>Submitted form <b>{String(p.formName ?? '')}</b>{contact}</span>;
    case 'IMPORTED':
      return <span>Imported from {String(p.filename ?? 'file')}{contact}</span>;
    case 'VERIFIED':
      return <span>Email verified: <b>{String(p.status ?? '')}</b>{p.vendor ? ` (${String(p.vendor)})` : ''}{contact}</span>;
    case 'MERGED':
      return <span><Typography.Text strong>{who}</Typography.Text> merged a duplicate into this record{contact}</span>;
    case 'UNMERGED':
      return <span><Typography.Text strong>{who}</Typography.Text> unmerged this record from {String(p.otherName ?? 'another contact')}{p.partial ? ' (deals, tasks and history stayed with the other contact)' : ''}{contact}</span>;
    default:
      return <span>{a.type}{contact}</span>;
  }
}
